import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, Brain, ArrowSquareOut, Calendar, Buildings, Tag, CurrencyDollar, Spinner } from "@phosphor-icons/react";
import { toast } from "sonner";
import { FavoriteButton } from "@/components/FavoriteButton";

const STATUS_COLORS = {
  Vigente: "bg-[#2A9D8F] text-white",
  Adjudicado: "bg-[#002FA7] text-white",
  Desierto: "bg-[#E63946] text-white",
};

export default function TenderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tender, setTender] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);

  useEffect(() => {
    api.get(`/tenders/${id}`)
      .then(res => setTender(res.data))
      .catch(() => toast.error("Error al cargar la licitación"))
      .finally(() => setLoading(false));
  }, [id]);

  const handleAnalyze = async () => {
    setAnalyzing(true);
    try {
      const res = await api.post(`/tenders/${id}/analyze`);
      setTender(prev => ({ ...prev, ai_summary: res.data.ai_summary }));
      toast.success("Análisis completado");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error en el análisis");
    } finally {
      setAnalyzing(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="tender-detail-loading">
        <Skeleton className="h-8 w-64 rounded-none" />
        <Skeleton className="h-48 rounded-none" />
        <Skeleton className="h-64 rounded-none" />
      </div>
    );
  }

  if (!tender) {
    return (
      <div className="text-center py-20">
        <p className="text-muted-foreground">Licitación no encontrada</p>
        <Button variant="outline" className="mt-4 rounded-none" onClick={() => navigate("/buscar")}>Volver</Button>
      </div>
    );
  }

  const formatDate = (d) => {
    if (!d) return "—";
    try { return new Date(d).toLocaleDateString("es-GT", { year: "numeric", month: "long", day: "numeric" }); }
    catch { return d; }
  };

  return (
    <div data-testid="tender-detail-page" className="space-y-6 max-w-5xl">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Button variant="ghost" size="icon" className="rounded-none mt-1 flex-shrink-0" onClick={() => navigate(-1)} data-testid="back-button">
          <ArrowLeft size={20} />
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="font-mono text-xs text-muted-foreground" data-testid="tender-nog">{tender.nog}</span>
            <Badge className={`rounded-none text-[10px] font-mono ${STATUS_COLORS[tender.status] || "bg-secondary"}`} data-testid="tender-status">
              {tender.status}
            </Badge>
            <FavoriteButton tenderId={tender.id} size={20} className="ml-auto" />
          </div>
          <h1 className="font-heading font-black text-2xl lg:text-3xl tracking-tight mt-2" data-testid="tender-title">
            {tender.title}
          </h1>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Details grid */}
          <Card className="rounded-none shadow-none border border-border/40">
            <CardContent className="p-0">
              <div className="grid grid-cols-2 divide-x divide-y divide-border/40">
                <InfoCell icon={Buildings} label="Entidad Compradora" value={tender.entity_name} testId="tender-entity" />
                <InfoCell icon={Tag} label="Categoría" value={tender.category} testId="tender-category" />
                <InfoCell icon={CurrencyDollar} label="Monto Estimado" value={`Q${(tender.amount || 0).toLocaleString()} ${tender.currency || "GTQ"}`} testId="tender-amount" />
                <InfoCell icon={Calendar} label="Fecha Publicación" value={formatDate(tender.publish_date)} testId="tender-pub-date" />
                <InfoCell icon={Calendar} label="Fecha Cierre" value={formatDate(tender.close_date)} testId="tender-close-date" />
                <div className="p-4">
                  <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-1">Fuente</p>
                  {tender.source_url ? (
                    <a href={tender.source_url} target="_blank" rel="noopener noreferrer" className="text-sm text-[#002FA7] hover:underline flex items-center gap-1" data-testid="tender-source-link">
                      {tender.source_site || "GuateCompras"} <ArrowSquareOut size={14} />
                    </a>
                  ) : (
                    <p className="text-sm">{tender.source_site || "—"}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Description */}
          <Card className="rounded-none shadow-none border border-border/40">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading font-bold text-sm">Descripción</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm leading-relaxed text-foreground/80" data-testid="tender-description">
                {tender.description || "No hay descripción disponible."}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* AI Analysis sidebar */}
        <div className="space-y-6">
          <Card className="rounded-none shadow-none border border-border/40 bg-[#002FA7]/[0.02]" data-testid="ai-analysis-card">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading font-bold text-sm flex items-center gap-2">
                <Brain size={18} weight="duotone" className="text-[#002FA7]" />
                Análisis IA (GPT-5.2)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {tender.ai_summary ? (
                <div className="text-sm leading-relaxed whitespace-pre-wrap" data-testid="ai-summary-content">
                  {tender.ai_summary}
                </div>
              ) : (
                <div className="text-center py-6">
                  <p className="text-sm text-muted-foreground mb-4">
                    Genera un análisis inteligente de esta licitación
                  </p>
                  <Button
                    data-testid="analyze-button"
                    className="rounded-none bg-[#002FA7] hover:bg-[#001D66] text-white"
                    onClick={handleAnalyze}
                    disabled={analyzing}
                  >
                    {analyzing ? (
                      <><Spinner size={16} className="mr-2 animate-spin" /> Analizando...</>
                    ) : (
                      <><Brain size={16} className="mr-2" /> Analizar con IA</>
                    )}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function InfoCell({ icon: Icon, label, value, testId }) {
  return (
    <div className="p-4">
      <div className="flex items-center gap-1.5 mb-1">
        <Icon size={14} weight="regular" className="text-muted-foreground" />
        <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{label}</p>
      </div>
      <p className="text-sm font-medium" data-testid={testId}>{value || "—"}</p>
    </div>
  );
}
